import type {KeycloakUserResponse, UsersStatsResponse} from "@/adapters/users.adapter";
import {toUser, toUsersPage, toUsersStats} from "@/adapters/users.adapter";
import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import type {User} from "@/types/user.types";

interface UsersFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  kycStatus?: string;
  country?: string;
}

type UsersListResult = ReturnType<typeof toUsersPage>;

export const getUsersService = async (filters: UsersFilters = {}): Promise<TResponseRequest<UsersListResult>> => {
  const raw = await httpRequest.get<KeycloakUserResponse[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/users`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: toUsersPage(raw.data, raw.pagination)};
};

export const getUserByIdService = async (userId: string): Promise<TResponseRequest<User>> => {
  const raw = await httpRequest.get<KeycloakUserResponse>({
    url: `${config.apiBaseUrl}/bo/users/${encodeURIComponent(userId)}`,
  });
  return {...raw, data: raw.data ? toUser(raw.data) : undefined};
};

export const createUserService = async (body: {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}): Promise<TResponseRequest<User>> => {
  const raw = await httpRequest.post<KeycloakUserResponse>({
    url: `${config.apiBaseUrl}/bo/users`,
    body,
  });
  return {...raw, data: raw.data ? toUser(raw.data) : undefined};
};

export const updateUserService = async (
  userId: string,
  body: Record<string, unknown>
): Promise<TResponseRequest<User>> => {
  const raw = await httpRequest.patch<KeycloakUserResponse>({
    url: `${config.apiBaseUrl}/bo/users/${encodeURIComponent(userId)}`,
    body,
  });
  return {...raw, data: raw.data ? toUser(raw.data) : undefined};
};

export const deleteUserService = async (userId: string): Promise<TResponseRequest<void>> => {
  return httpRequest.delete<void>({
    url: `${config.apiBaseUrl}/bo/users/${encodeURIComponent(userId)}`,
    body: {},
  });
};

export const toggleUserStatusService = async (userId: string, enabled: boolean): Promise<TResponseRequest<void>> => {
  return httpRequest.patch<void>({
    url: `${config.apiBaseUrl}/bo/users/${encodeURIComponent(userId)}/status`,
    body: {enabled},
  });
};

export const resetUserPasswordService = async (userId: string): Promise<TResponseRequest<void>> => {
  return httpRequest.post<void>({
    url: `${config.apiBaseUrl}/bo/users/${encodeURIComponent(userId)}/reset-password`,
    body: {},
  });
};

export const getUsersStatsService = async (): Promise<TResponseRequest<UsersStatsResponse>> => {
  const raw = await httpRequest.get<UsersStatsResponse>({
    url: `${config.apiBaseUrl}/bo/users/statistics`,
  });
  return {...raw, data: raw.data ? toUsersStats(raw.data) : undefined};
};

export const searchUsersService = async (
  query: string,
  page = 1,
  limit = 20
): Promise<TResponseRequest<UsersListResult>> => {
  const raw = await httpRequest.get<KeycloakUserResponse[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/users/search`, {q: query, page, limit}),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: toUsersPage(raw.data, raw.pagination)};
};
